/**
 * STT 엔진 — 등록 · 교체 팝업 (2026-09-21 · 시안 ④).
 *
 * ## ★★★ 끝점은 «호스트까지만»
 *
 * 붙은 서버는 OpenAI 호환이라 경로를 **규약이 정한다**(`/v1/audio/transcriptions`). 전체 URL 을 받으면
 * 운영자가 `/v1/models` 를 넣어도 저장되고, **틀린 것이 저장 시점에 안 잡힌다.** 규칙과 까닭은
 * `sttEndpoint.ts` 에 있고 여기서는 그 판정을 쓰기만 한다 — 같은 규칙이 두 벌이 되지 않게.
 *
 * ## ⛔ 연결 시험은 «전사 끝점»으로 친다 — 이 팝업이 그 사실을 말한다
 *
 * 실측(2026-09-21 · 관제): 이 서버는 **`/health` 가 키 «없이» 200** 을 내고 **전사만 401** 이다.
 * 즉 「닿나」만 재면 **키가 틀려도 「정상」**이 나온다. 그래서 화면은 판정을 「정상」이라 쓰지 않고
 * **무엇을 쟀는지**를 함께 적는다. ⛔ 저장만으로 「동작 중」이 되지 않는 규칙이 여기에 걸려 있다.
 *
 * ## ⚠ 평문(http)이면 «말만» 한다 — 막지 않는다
 *
 * 이 주소로 가는 것은 **회의 녹음 원음**이다. 평문이면 같은 망을 지나는 누구나 들을 수 있다.
 * 그런데 실제 서버가 사내 `http` 라 **막으면 쓸 수가 없다**(2026-09-21 사용자 결정 — http 허용).
 * ⛔ **경고가 저장을 막지 않는다.** 이 규칙을 시험으로 잠가 뒀다 — 다음 사람이 「더 안전하게」 하려다
 * 막아 버리는 것이 이 자리에서 실제로 일어난 일이기 때문이다(서버 #1590 이 https 만 받게 지어졌다).
 *
 * ## ★★★ `config` 는 «통째로» 바뀐다 — 셋을 늘 함께 보낸다
 *
 * 서버의 PATCH 는 `config` 를 **부분 병합하지 않는다.** 끝점만 보내면 `model`·`timeout_sec` 이
 * **사라진다.** 증상은 「고쳤는데 안 되네」다. 그래서 이 팝업은 **늘 셋을 함께** 만든다(`config()`).
 * ⛔ 그리고 `config` 에 **키를 넣지 않는다** — `config` 는 암호화되지 않아 평문으로 표에 남고,
 * 서버도 키 류 이름을 400 으로 막는다.
 *
 * ## 키
 *
 * 옛 키를 보여 주는 길이 없다. 바꾸는 길은 새 키로 덮어쓰기뿐이고, 비우면 그대로 둔다
 * — 날씨 팝업과 **같은 규칙**이다(`ExternalApiEditModal`).
 */
import { useState } from 'react';
import { X, AlertTriangle } from '../../../icons';
import {
  createProvider, patchProvider, deleteProvider, testKey, readSttConfig,
  type ExternalApiAdapter, type ExternalApiProvider, type ProbeResult,
} from '../../../api/externalApis';
import { errText } from './ExternalApiEditModal';
import { checkSttEndpoint, checkSttTimeout, sttCallUrl, sttEndpointWarning, STT_PATH, STT_TIMEOUT_DEFAULT_SEC } from './sttEndpoint';

/**
 * ⛔ 「정상」도 「전사까지 됐다」도 아니다 — **두 겹으로 세다** (2026-09-21 실측).
 *
 *   ① 서버가 시험에 올리는 것은 **무음 WAV** 다(`stt-adapters.ts` `silentWav`). 그래서
 *      확인되는 것은 **「전사 끝점이 이 키를 받아들였다」**까지다 — 말이 제대로 받아쓰기되는지가 아니다.
 *   ② 그리고 이 엔진은 **`model` 을 무시한다**(관제 실측: 모델 이름만 바꿔 보낸 세 번이
 *      **바이트까지 같은** 결과였다). 즉 시험이 통과해도 **모델이 맞는지는 모른다.**
 *
 * ★ 그래서 판정 글자를 **「전사 끝점이 받았습니다」**로 적는다 — 잰 것만 말한다.
 */
const VERDICT_LABEL: Record<string, string> = {
  ok: '전사 끝점이 받았습니다',
  auth_failed: '키 거절',
  shared_quota_exhausted: '한도 소진',
  provider_error: '엔진 오류',
  unreachable: '닿지 못함',
};

interface Props {
  /** null 이면 새로 등록. */
  provider: ExternalApiProvider | null;
  /** 고를 수 있는 것 — `category === 'stt'` 이고 아직 등록 안 된 것. */
  adapters: ExternalApiAdapter[];
  onClose: () => void;
  onSaved: (message: string) => void;
  /**
   * ⭐ 2026-10-23 — 「엔진 종류」에서 화자 구분을 고르면 화자 구분 등록 창으로 넘긴다.
   *   운영에서 「엔진 종류에 화자 구분이 없어 등록할 수 없다」는 지적을 받았다 — 화자 구분은
   *   왼쪽 「화자 구분」 칸에서만 등록되는데, 사람은 받아쓰기 엔진 옆에서 찾는다.
   *   ⇒ 이 창이 화자 구분을 «저장»하지는 않는다(끝점·키 칸이 다르다) — 길만 잇는다. 없으면 종전 그대로.
   */
  onPickDiarize?: () => void;
}

/** 「엔진 종류」 목록에서 화자 구분 줄을 가르는 값 머리 — 어댑터 키와 겹치지 않는다. */
const DIARIZE_PICK = '__diarize__:';

export function SttEditModal({ provider, adapters, onClose, onSaved, onPickDiarize }: Props) {
  const isNew = provider === null;
  const choices = adapters.filter((a) => a.category === 'stt' && !a.registered);
  const diarizeChoices = onPickDiarize ? adapters.filter((a) => a.category === 'diarize' && !a.registered) : [];
  const saved = provider ? readSttConfig(provider) : null;

  const [providerKey, setProviderKey] = useState(isNew ? (choices[0]?.provider_key ?? '') : provider.provider_key);
  const [endpoint, setEndpoint] = useState(saved?.endpoint ?? '');
  const [model, setModel] = useState(saved?.model ?? '');
  const [timeout, setTimeout_] = useState(saved ? String(saved.timeout_sec) : String(STT_TIMEOUT_DEFAULT_SEC));
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);

  const ep = checkSttEndpoint(endpoint);
  const to = checkSttTimeout(timeout);
  // 적는 중에는 나무라지 않는다 — 비어 있는 것은 「아직 안 적었다」이지 틀린 것이 아니다.
  const epError = endpoint.trim() !== '' && !ep.ok ? ep.message : null;
  const toError = !to.ok ? to.message : null;
  const callUrl = sttCallUrl(endpoint);
  // ⚠ 경고다 — 오류가 아니다. 저장 단추의 잠김 조건에 «넣지 않는다»(위 머리말).
  const plaintextWarning = sttEndpointWarning(endpoint);
  // ★ 서버가 «빈 모델»을 400 으로 거절한다(`stt-adapters.ts` readSttConfig) — 화면에서 먼저 막는다.
  //   막지 않으면 저장을 눌러야 비로소 알게 되고, 그때는 무엇이 틀렸는지 화면이 안 가리킨다.
  const modelMissing = model.trim() === '';

  /**
   * ★★★ 늘 **셋을 함께** 만든다 — 서버가 `config` 를 통째로 바꾸기 때문이다(머리말).
   * ⛔ 키는 여기 들어가지 않는다 — 암호화되지 않는 칸이다.
   */
  function config(): Record<string, unknown> | null {
    if (!ep.ok || !to.ok || modelMissing) return null;
    return { endpoint: ep.value, model: model.trim(), timeout_sec: to.value };
  }

  async function runTest() {
    if (!apiKey.trim()) { setError('시험할 키를 적으십시오.'); return; }
    if (!ep.ok) { setError(ep.message); return; }
    setBusy(true); setError(null); setProbe(null);
    try {
      // ★ 저장 «전» 시험에도 끝점을 함께 보낸다 — 어디로 부를지 없이 재면 무엇을 잰 것인지 모른다.
      setProbe(await testKey(providerKey, apiKey.trim(), config() ?? undefined));
    } catch (e: unknown) {
      setError(errText(e, '연결 시험에 실패했습니다'));
    } finally { setBusy(false); }
  }

  async function save() {
    const cfg = config();
    if (!cfg) {
      /*
       * ⚠ 저장 단추가 잠겨 있어 여기까지 오지 않는 것이 정상이다 — 그래도 «말없이» 끝내지 않는다.
       *
       * 셋 중 하나라서 못 만든 것이다:
       *   · 끝점이 틀림      → `epError`
       *   · 타임아웃이 틀림  → `toError`
       *   · 끝점이 **비었음** → 적는 중이라 `epError` 를 일부러 안 만든다 → `ep.message` 가 말한다
       *   · 모델이 **비었음** → 위 셋 다 없다 ★ 이때 아무 말도 안 하면 「눌러도 아무 일 없는 단추」다
       */
      setError(epError ?? toError ?? (ep.ok ? '모델 이름을 적으십시오.' : ep.message));
      return;
    }
    setBusy(true); setError(null);
    try {
      if (isNew) {
        if (!providerKey) { setError('엔진을 고르십시오.'); setBusy(false); return; }
        await createProvider({
          provider_key: providerKey,
          enabled,
          config: cfg,
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        });
        onSaved('엔진을 등록했습니다 — 연결 시험을 해야 「동작 중」이 됩니다.');
      } else {
        await patchProvider(provider.provider_key, {
          enabled,
          config: cfg,
          // 비우면 «안 바꾼다» — 날씨 팝업과 같은 규칙.
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        });
        onSaved('저장했습니다 — 연결 시험을 해야 「동작 중」이 됩니다.');
      }
    } catch (e: unknown) {
      setError(errText(e, '저장하지 못했습니다'));
      setBusy(false);
    }
  }

  async function removeProvider() {
    if (isNew) return;
    setBusy(true); setError(null);
    try {
      await deleteProvider(provider.provider_key);
      onSaved('엔진을 지웠습니다.');
    } catch (e: unknown) {
      setError(errText(e, '지우지 못했습니다'));
      setBusy(false);
    }
  }

  return (
    <div className="cl-modal__backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="cl-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isNew ? 'STT 엔진 등록' : provider.label}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cl-modal__head">
          <h3>{isNew ? 'STT 엔진 등록' : provider.label}</h3>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="cl-modal__body">
          {isNew && (
            <div className="cl-field">
              <label htmlFor="stt-engine">엔진 종류</label>
              <select
                id="stt-engine"
                className="xa-select"
                value={providerKey}
                onChange={(e) => {
                  if (onPickDiarize && e.target.value.startsWith(DIARIZE_PICK)) { onPickDiarize(); return; }
                  setProviderKey(e.target.value);
                }}
              >
                {choices.length === 0 && <option value="">등록할 수 있는 엔진이 없습니다</option>}
                {choices.map((a) => <option key={a.provider_key} value={a.provider_key}>{a.default_label}</option>)}
                {diarizeChoices.length > 0 && (
                  <optgroup label="화자 구분 — 고르면 화자 구분 등록으로 넘어갑니다">
                    {diarizeChoices.map((a) => <option key={a.provider_key} value={DIARIZE_PICK + a.provider_key}>{a.default_label}</option>)}
                  </optgroup>
                )}
              </select>
              <p className="xa-hint">부를 줄 아는 엔진만 고를 수 있습니다 — 목록에 없으면 서버에 코드가 먼저 필요합니다.</p>
              {diarizeChoices.length > 0 && (
                <p className="xa-hint">화자 구분 엔진은 왼쪽 「화자 구분」 칸에서 등록합니다 — 위 목록에서 고르면 그 창이 열립니다.</p>
              )}
            </div>
          )}

          <div className="cl-field">
            <label htmlFor="stt-endpoint">끝점</label>
            <input
              id="stt-endpoint"
              className="xa-input"
              value={endpoint}
              placeholder="http://ms-ssh.tchain.cloud:9293"
              onChange={(e) => { setEndpoint(e.target.value); setProbe(null); }}
            />
            {/* ★ 무엇을 부를지 보여 준다 — 「호스트까지만」이 왜인지가 여기서 드러난다. */}
            <p className="xa-hint">
              {callUrl
                ? <>실제로는 <b>{callUrl}</b> 를 부릅니다.</>
                : <>호스트까지만 적습니다 — <b>{STT_PATH}</b> 는 규약이 정하고 서버가 붙입니다.</>}
            </p>
            {epError && <p className="xa-hint xa-danger" role="alert">{epError}</p>}
          </div>

          {/* ⚠ 막지 않고 «말만» 한다 — https 에는 띄우지 않는다(늘 뜨는 경고는 아무도 안 읽는다). */}
          {plaintextWarning && (
            <div className="xa-band xa-band--warn" role="status">
              <AlertTriangle size={18} aria-hidden />
              <span className="xa-band__t">
                <b>{plaintextWarning}</b>
                <span>저장은 됩니다 — 알고 쓰시라고 알리는 것입니다.</span>
              </span>
            </div>
          )}

          <div className="cl-field">
            <label htmlFor="stt-model">모델</label>
            <input id="stt-model" className="xa-input" value={model} placeholder="large-v3-turbo" onChange={(e) => setModel(e.target.value)} />
            <p className="xa-hint">
              엔진이 어느 모델로 전사할지입니다 — 비워 둘 수 없습니다.<br />
              ⚠ <b>엔진에 따라 이 값을 무시할 수 있습니다.</b> 연결 시험은 모델이 맞는지까지는 확인하지 않습니다.
            </p>
          </div>

          <div className="cl-field">
            <label htmlFor="stt-timeout">타임아웃(초)</label>
            <input id="stt-timeout" className="xa-input" inputMode="numeric" value={timeout} onChange={(e) => setTimeout_(e.target.value)} />
            <p className="xa-hint">비우면 {STT_TIMEOUT_DEFAULT_SEC}초를 씁니다.</p>
            {toError && <p className="xa-hint xa-danger" role="alert">{toError}</p>}
          </div>

          {!isNew && (
            <div className="cl-field">
              <label>지금 키</label>
              <div className="xa-readonly">
                {provider.key_state === 'stored' && (provider.key_hint
                  ? <b>{provider.key_hint}</b>
                  // ⚠ 끝 넉 자를 «모르는» 줄이 있다 — 마이그레이션 전에 저장된 키. 가짜를 만들지 않는다.
                  : <span>저장돼 있습니다 · 끝 넉 자는 다음에 키를 넣을 때부터 보입니다</span>)}
                {provider.key_state === 'none' && <span>등록된 키가 없습니다</span>}
                {provider.key_state === 'unreadable' && <span className="xa-badge xa-badge--bad">키를 못 읽음 — 다시 넣어야 합니다</span>}
              </div>
            </div>
          )}

          <div className="cl-field">
            <label htmlFor="stt-key">{isNew ? '키' : '새 키로 교체'}</label>
            <input
              id="stt-key"
              className="xa-input"
              type="password"
              autoComplete="off"
              value={apiKey}
              placeholder={isNew ? '엔진에 적어 둔 인증키를 붙여 넣습니다' : '새 인증키 (비우면 그대로 둡니다)'}
              onChange={(e) => { setApiKey(e.target.value); setProbe(null); }}
            />
            <p className="xa-hint">
              {isNew
                ? '⚠ 저장하면 다시는 보이지 않습니다 — 끝 네 자리만 남습니다.'
                : '교체하면 옛 키는 사라집니다 — 되돌릴 수 없습니다.'}
            </p>
          </div>

          <label className="xa-check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            이 엔진을 씁니다
          </label>

          {/* ⛔ 실측으로 확인된 함정을 «키를 넣는 자리»에서 말한다. */}
          <div className="xa-band xa-band--warn">
            <AlertTriangle size={18} aria-hidden />
            <span className="xa-band__t">
              <b>연결 시험은 전사 끝점으로 칩니다</b>
              <span>이 엔진은 <b>상태 확인 주소가 키 없이도 응답</b>합니다 — 「닿나」만 재면 <b>키가 틀려도 정상으로 보입니다.</b></span>
            </span>
          </div>

          {probe && (
            <div className={`xa-band ${probe.verdict === 'ok' ? 'xa-band--ok' : 'xa-band--bad'}`} role="status">
              <AlertTriangle size={18} aria-hidden />
              <span className="xa-band__t">
                <b>{VERDICT_LABEL[probe.verdict] ?? probe.verdict}{probe.http_status ? ` · HTTP ${probe.http_status}` : ''}</b>
                <span>{probe.message}</span>
              </span>
            </div>
          )}
          {error && (
            <div className="xa-band xa-band--bad" role="alert">
              <AlertTriangle size={18} aria-hidden />
              <span className="xa-band__t"><b>{error}</b></span>
            </div>
          )}
        </div>

        <div className="cl-modal__foot">
          {!isNew && <button type="button" className="cl-modal__btn xa-danger" disabled={busy} onClick={() => void removeProvider()}>엔진 삭제</button>}
          <span className="xa-spacer" />
          <button type="button" className="cl-modal__btn" disabled={busy || !apiKey.trim() || !ep.ok || modelMissing || !providerKey} onClick={() => void runTest()}>연결 시험</button>
          <button type="button" className="cl-modal__btn" disabled={busy} onClick={onClose}>취소</button>
          <button type="button" className="cl-modal__btn cl-modal__btn--primary" disabled={busy || !ep.ok || !to.ok || modelMissing} onClick={() => void save()}>
            {isNew ? '등록' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
